import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { ArrowDownIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from './icons';

type SelectionMode = 'range' | 'single';

interface DateRangeFilterProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

interface CalendarDay {
  value: string;
  day: number;
  inCurrentMonth: boolean;
}

function dateParts(value: string): [number, number, number] {
  const parts = value.split('-').map(Number);
  return [parts[0] ?? 0, parts[1] ?? 1, parts[2] ?? 1];
}

function toIso(year: number, month: number, day: number): string {
  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

function monthValue(value: string): string {
  const [year, month] = dateParts(value);
  return toIso(year, month, 1);
}

export function moveMonth(value: string, offset: number): string {
  const [year, month] = dateParts(value);
  const date = new Date(Date.UTC(year, month - 1 + offset, 1));
  return toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
}

export function buildCalendar(value: string): CalendarDay[] {
  const [year, month] = dateParts(monthValue(value));
  const first = new Date(Date.UTC(year, month - 1, 1));
  const mondayOffset = (first.getUTCDay() + 6) % 7;
  const start = new Date(Date.UTC(year, month - 1, 1 - mondayOffset));

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start.getTime() + index * 86_400_000);
    return {
      value: toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate()),
      day: date.getUTCDate(),
      inCurrentMonth: date.getUTCMonth() + 1 === month
    };
  });
}

export function normalizeRange(first: string, second: string): [string, string] {
  return first <= second ? [first, second] : [second, first];
}

function formatDate(value: string): string {
  if (!value) return '—';
  const [year, month, day] = dateParts(value);
  return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
}

function monthTitle(value: string): string {
  const [year, month] = dateParts(value);
  return `Tháng ${month} ${year}`;
}

function todayValue(): string {
  const today = new Date();
  return toIso(today.getFullYear(), today.getMonth() + 1, today.getDate());
}

function DateCalendar({
  month,
  rangeStart,
  rangeEnd,
  today,
  showPrevious,
  showNext,
  onPrevious,
  onNext,
  onChoose,
  onHover
}: {
  month: string;
  rangeStart: string;
  rangeEnd: string;
  today: string;
  showPrevious: boolean;
  showNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onChoose: (value: string) => void;
  onHover: (value: string) => void;
}) {
  const days = useMemo(() => buildCalendar(month), [month]);

  return (
    <section className="date-picker-calendar" aria-label={monthTitle(month)}>
      <header className="date-picker-month-header">
        {showPrevious ? <button type="button" onClick={onPrevious} title="Tháng trước" aria-label="Tháng trước"><ChevronLeftIcon /></button> : <span />}
        <strong>{monthTitle(month)}</strong>
        {showNext ? <button type="button" onClick={onNext} title="Tháng sau" aria-label="Tháng sau"><ChevronRightIcon /></button> : <span />}
      </header>
      <div className="date-picker-weekdays" aria-hidden="true">
        {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="date-picker-days">
        {days.map((day) => {
          const selectedEdge = day.value === rangeStart || day.value === rangeEnd;
          const inRange = Boolean(rangeStart && rangeEnd && day.value >= rangeStart && day.value <= rangeEnd);
          return (
            <button
              type="button"
              key={day.value}
              className={`${day.inCurrentMonth ? '' : 'outside-month'}${inRange ? ' in-range' : ''}${selectedEdge ? ' selected-edge' : ''}${day.value === today ? ' today' : ''}`}
              disabled={!day.inCurrentMonth}
              aria-label={`Chọn ngày ${formatDate(day.value)}`}
              aria-pressed={selectedEdge}
              onClick={() => onChoose(day.value)}
              onMouseEnter={() => onHover(day.value)}
            >
              {day.day}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function DateRangeFilter({ from, to, onChange }: DateRangeFilterProps) {
  const popoverId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<SelectionMode>(from === to ? 'single' : 'range');
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [anchorMonth, setAnchorMonth] = useState(monthValue(from));
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [hoveredDate, setHoveredDate] = useState('');
  const today = useMemo(todayValue, []);

  const previewRange = useMemo(() => {
    if (mode === 'range' && selectingEnd && draftFrom && hoveredDate) return normalizeRange(draftFrom, hoveredDate);
    if (!draftFrom || !draftTo) return [draftFrom, draftTo] as [string, string];
    return normalizeRange(draftFrom, draftTo);
  }, [draftFrom, draftTo, hoveredDate, mode, selectingEnd]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  function openPicker() {
    if (open) {
      setOpen(false);
      return;
    }
    setDraftFrom(from);
    setDraftTo(to);
    setAnchorMonth(monthValue(from));
    setSelectingEnd(false);
    setHoveredDate('');
    setOpen(true);
  }

  function changeMode(nextMode: SelectionMode) {
    setMode(nextMode);
    setSelectingEnd(false);
    setHoveredDate('');
    if (nextMode === 'single') {
      const selected = draftTo || draftFrom || today;
      setDraftFrom(selected);
      setDraftTo(selected);
      setAnchorMonth(monthValue(selected));
    }
  }

  function chooseDate(value: string) {
    if (mode === 'single') {
      setDraftFrom(value);
      setDraftTo(value);
      return;
    }
    if (!selectingEnd) {
      setDraftFrom(value);
      setDraftTo('');
      setSelectingEnd(true);
      setHoveredDate(value);
      return;
    }
    const [start, end] = normalizeRange(draftFrom, value);
    setDraftFrom(start);
    setDraftTo(end);
    setSelectingEnd(false);
    setHoveredDate('');
  }

  function applySelection() {
    if (!draftFrom || !draftTo) return;
    const [start, end] = normalizeRange(draftFrom, draftTo);
    onChange(start, mode === 'single' ? start : end);
    setOpen(false);
  }

  const triggerText = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  const selectionText = mode === 'single'
    ? formatDate(draftFrom)
    : selectingEnd
      ? `${formatDate(draftFrom)} – Chọn ngày kết thúc`
      : `${formatDate(draftFrom)} – ${formatDate(draftTo)}`;

  return (
    <div className="date-range-filter" ref={rootRef}>
      <button
        type="button"
        className={`date-range-trigger ${open ? 'is-open' : ''}`}
        aria-label="Chọn ngày lọc"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={openPicker}
      >
        <CalendarIcon />
        <span><small>{from === to ? 'Ngày' : 'Khoảng ngày'}</small><strong>{triggerText}</strong></span>
        <ArrowDownIcon />
      </button>

      {open ? (
        <div id={popoverId} className={`date-picker-popover ${mode === 'single' ? 'single-mode' : ''}`} role="dialog" aria-label="Chọn ngày lọc">
          <div className="date-picker-mode" role="group" aria-label="Kiểu lọc ngày">
            <button type="button" className={mode === 'range' ? 'active' : ''} aria-pressed={mode === 'range'} onClick={() => changeMode('range')}>Khoảng ngày</button>
            <button type="button" className={mode === 'single' ? 'active' : ''} aria-pressed={mode === 'single'} onClick={() => changeMode('single')}>Một ngày</button>
          </div>

          <div className="date-picker-calendars">
            <DateCalendar
              month={anchorMonth}
              rangeStart={previewRange[0]}
              rangeEnd={previewRange[1]}
              today={today}
              showPrevious
              showNext={mode === 'single'}
              onPrevious={() => setAnchorMonth((current) => moveMonth(current, -1))}
              onNext={() => setAnchorMonth((current) => moveMonth(current, 1))}
              onChoose={chooseDate}
              onHover={setHoveredDate}
            />
            {mode === 'range' ? (
              <DateCalendar
                month={moveMonth(anchorMonth, 1)}
                rangeStart={previewRange[0]}
                rangeEnd={previewRange[1]}
                today={today}
                showPrevious={false}
                showNext
                onPrevious={() => setAnchorMonth((current) => moveMonth(current, -1))}
                onNext={() => setAnchorMonth((current) => moveMonth(current, 1))}
                onChoose={chooseDate}
                onHover={setHoveredDate}
              />
            ) : null}
          </div>

          <footer className="date-picker-footer">
            <span>{selectionText}</span>
            <div>
              <button type="button" className="secondary-action" onClick={() => setOpen(false)}>Hủy</button>
              <button type="button" className="primary-action" disabled={!draftFrom || !draftTo || selectingEnd} onClick={applySelection}>Áp dụng</button>
            </div>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
